<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { useAppNav } from '@/shared/composables/useAppNav';
import { useAuth } from '@/shared/composables/useAuth';
import { useTheme } from '@/shared/composables/useTheme';
import { useTicketNotifications } from '@/shared/composables/useTicketNotifications';

import AppNavItems from './AppNavItems.vue';
import type { AppNavItem } from './nav-items';

const route = useRoute();
const { currentUser, isGranted, logout } = useAuth();
const { open: drawerOpen, closeNav } = useAppNav();
const { canSee: canSeeNotifications, unreadCount } = useTicketNotifications();
const { preference: themePreference } = useTheme();

/**
 * Expanded state of the desktop rail.
 *
 * Driven from JS rather than a bare `group-hover:` so one boolean governs
 * everything that has to agree — width, label visibility, tooltip suppression,
 * `aria-expanded` — and so keyboard focus can expand it too. The rail itself
 * never moves: it reserves a fixed 48px in the flow and the panel that grows is
 * absolutely positioned on top, which is why the conversation column stays put.
 */
const expanded = ref(false);

/**
 * The user menu renders in a portal, so moving the pointer into it — or into
 * its Appearance submenu — leaves the rail. Without this the rail would
 * collapse under an open menu and drag the menu's anchor with it.
 */
const userMenuOpen = ref(false);

// A short close delay keeps the panel from flickering when the pointer crosses
// the shadow edge or skims past on its way somewhere else.
const CLOSE_DELAY_MS = 120;

let closeTimer: ReturnType<typeof setTimeout> | undefined;

const expand = () => {
  clearTimeout(closeTimer);
  expanded.value = true;
};

const collapse = () => {
  clearTimeout(closeTimer);

  if (userMenuOpen.value) {
    return;
  }

  closeTimer = setTimeout(() => {
    expanded.value = false;
  }, CLOSE_DELAY_MS);
};

watch(userMenuOpen, (open) => {
  if (open) {
    expand();
  } else {
    collapse();
  }
});

const onFocusOut = (event: FocusEvent) => {
  const next = event.relatedTarget as Node | null;

  // Focus moving between two children of the rail is not a focus-out for us.
  if (next && (event.currentTarget as HTMLElement).contains(next)) {
    return;
  }

  collapse();
};

onBeforeUnmount(() => clearTimeout(closeTimer));

const navigationItems = computed<AppNavItem[]>(() => {
  const items: AppNavItem[] = [];

  if (isGranted('st:core:tickets:list')) {
    items.push({
      label: 'Tickets',
      icon: 'i-lucide-inbox',
      to: '/tickets',
      active: ['Tickets', 'Ticket'].includes(String(route.name)),
      testId: 'nav-tickets',
    });
  }

  if (canSeeNotifications.value) {
    items.push({
      label: 'Notifications',
      icon: 'i-lucide-bell',
      to: '/notifications',
      active: String(route.name) === 'Notifications',
      // Only when there is something. A badge reading 0 is a control that
      // demands attention to report that no attention is needed.
      badge: unreadCount.value > 0 ? unreadCount.value : undefined,
      testId: 'nav-notifications',
    });
  }

  if (isGranted('st:core:publishers:list')) {
    items.push({
      label: 'Publishers',
      icon: 'i-lucide-radio-tower',
      to: '/publishers',
      active: String(route.name).startsWith('Publisher'),
      testId: 'nav-publishers',
    });
  }

  if (isGranted('st:core:users:create')) {
    items.push({
      label: 'Users',
      icon: 'i-lucide-users',
      to: '/users',
      active: String(route.name).startsWith('User'),
      testId: 'nav-users',
    });
  }

  // Manage-gated (managers/admins): clients and plain users can already see
  // org labels through pickers and filters; the settings page is for editing.
  if (isGranted('st:core:organizations:create')) {
    items.push({
      label: 'Organizations',
      icon: 'i-lucide-building-2',
      to: '/settings/organizations',
      active: String(route.name).startsWith('Organization'),
      testId: 'nav-organizations',
    });
  }

  return items;
});

const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
] as const;

const userMenuItems = computed(() => [[{
  label: 'My profile',
  icon: 'i-lucide-user',
  to: '/me/edit',
}, ...(canSeeNotifications.value
  ? [{
      label: 'Notification settings',
      icon: 'i-lucide-bell',
      to: '/settings/notifications',
    }]
  : [])], [{
  label: 'Appearance',
  icon: 'i-lucide-sun-moon',
  children: THEME_OPTIONS.map((option) => ({
    label: option.label,
    type: 'checkbox' as const,
    checked: themePreference.value === option.value,
    onSelect: () => {
      themePreference.value = option.value;
    },
  })),
}], [{
  label: 'Sign out',
  icon: 'i-lucide-log-out',
  onSelect: () => logout(),
}]]);

const avatarUrl = computed(() => currentUser.value.avatar?.renditions?.[0]?.url);
</script>

<template>
  <!-- Desktop: a fixed 48px slot in the flow. The panel inside overflows it on
       hover instead of widening it, so nothing downstream re-lays out. -->
  <div
    class="relative hidden w-12 shrink-0 lg:block"
    data-testid="app-nav-rail"
    @mouseenter="expand"
    @mouseleave="collapse"
    @focusin="expand"
    @focusout="onFocusOut"
    @keydown.escape="expanded = false"
  >
    <nav
      class="absolute inset-y-0 left-0 z-40 flex flex-col overflow-hidden border-e bg-muted transition-[width,background-color,box-shadow,border-color] duration-[220ms] ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none"
      :class="expanded ? 'w-52 border-default bg-elevated shadow-xl' : 'w-12 border-transparent'"
      :aria-expanded="expanded"
      aria-label="Main navigation"
      :data-expanded="expanded"
    >
      <!-- Fixed inner width: the content is laid out at its final size and
           merely clipped while collapsed, so labels never reflow mid-animation. -->
      <div class="flex h-full w-52 flex-col px-2 py-2">
        <UDropdownMenu
          v-model:open="userMenuOpen"
          :items="userMenuItems"
          :ui="{ content: 'min-w-56' }"
        >
          <button
            type="button"
            class="flex h-8 w-full items-center gap-2 rounded-lg px-1 text-left transition-colors hover:bg-elevated/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            data-testid="sidebar-user-button"
            :aria-label="expanded ? undefined : currentUser.fullName"
          >
            <span class="grid size-6 shrink-0 place-items-center">
              <UAvatar
                :src="avatarUrl"
                :alt="currentUser.fullName"
                size="2xs"
              />
            </span>

            <span
              v-show="expanded"
              class="truncate text-sm font-medium text-highlighted"
              data-testid="sidebar-user-name"
            >
              {{ currentUser.fullName }}
            </span>

            <UIcon
              v-show="expanded"
              name="i-lucide-chevron-down"
              class="ms-auto size-3.5 shrink-0 text-dimmed"
            />
          </button>
        </UDropdownMenu>

        <div class="mt-2 min-h-0 flex-1 overflow-y-auto">
          <AppNavItems
            :items="navigationItems"
            :expanded="expanded"
          />
        </div>
      </div>
    </nav>
  </div>

  <!-- Below `lg` there is no hover, so the same navigation arrives as a drawer
       opened from each page's navbar. -->
  <USlideover
    v-model:open="drawerOpen"
    side="left"
    title="Navigation"
    :ui="{ content: 'w-64 max-w-[80vw]' }"
  >
    <template #body>
      <div
        class="flex flex-col gap-2"
        data-testid="app-nav-drawer"
      >
        <AppNavItems
          :items="navigationItems"
          expanded
          @navigate="closeNav"
        />
      </div>
    </template>
  </USlideover>
</template>
